/**
 * AppsMarketplaceSection — the App Marketplace rendered as a section
 * inside MarketplacePage. No outer `<div className="marketplace-page">`
 * wrapper; the parent owns that. Use this when you want the apps tiles
 * to sit alongside / below the workflow tiles on the same page.
 *
 * For the standalone deep-link `/marketplace/apps`, AppsMarketplacePage
 * wraps this same component with its own page wrapper.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IonIcon } from '@ionic/react';
import { rocketOutline, openOutline, alertCircle, star, starOutline } from 'ionicons/icons';

import { Apps } from '../../services/apps';
import ProjectsService from '../../services/projects';
import authService from '../../services/auth';
import Credits, { CREDIT_CHANGED_EVENT, formatBalance, hoursOfRuntime } from '../../services/credits';
import Field from '../../components/atoms/Field/Field';
import AppPricingBadge, { resolveTier } from '../../components/billing/AppPricingBadge';
import '../MarketplacePage/MarketplaceDetailModal.css';
import './AppsMarketplacePage.css';

/**
 * @param {Object} props
 * @param {boolean} [props.showHeader=true]  Render the section's own h2 +
 *   subtitle. Pass false when the parent page already provides the
 *   heading (e.g. the standalone /marketplace/apps wrapper).
 */
const AppsMarketplaceSection = ({ showHeader = true }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Deploy modal state
  const [active, setActive] = useState(null);     // catalog tile (list-shape)
  const [activeDetail, setActiveDetail] = useState(null); // full detail (with myRating)
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [pickedProject, setPickedProject] = useState('');
  const [instanceName, setInstanceName] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployError, setDeployError] = useState(null);

  // Rating UX state (mirrors workflow modal)
  const [hoverRating, setHoverRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);

  // Credit balance for the cost banner in the deploy modal. null = not
  // yet fetched. Reloaded on every modal-open + when the global
  // `credit-changed` event fires (post-checkout return).
  const [creditBalanceMc, setCreditBalanceMc] = useState(null);

  const { projectId: projectScopedId } = useParams();
  const navigate = useNavigate();
  const isAuthed = authService.isAuthenticated?.() ?? false;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await Apps.listCatalog();
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load apps catalog:', err);
        setError(err.message || 'Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // When the URL is project-scoped (/projects/:id/marketplace) we skip
  // the project-picker entirely — the user already chose. We still
  // fetch the project NAME so the modal can show which project the
  // instance lands in. Outside a project, we fetch the full list so
  // the user can pick.
  const ensureProjects = useCallback(async () => {
    if (projectScopedId) {
      // Already inside a project — pin to that id and resolve its name
      // for display. A single-project fetch would be cheaper but the
      // shared service only exposes a list call; the result is cached.
      if (pickedProject === projectScopedId && projects.length > 0) return;
      setProjectsLoading(true);
      try {
        const res = await ProjectsService.getProjects();
        const list = res?.projects || [];
        setProjects(list);
        setPickedProject(projectScopedId);
      } catch (err) {
        console.error('Failed to resolve project name:', err);
        setPickedProject(projectScopedId);
      } finally {
        setProjectsLoading(false);
      }
      return;
    }
    if (projects.length > 0 || projectsLoading) return;
    setProjectsLoading(true);
    try {
      const res = await ProjectsService.getProjects();
      const list = res?.projects || [];
      setProjects(list);
      setPickedProject(list[0]?.id || '');
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, [projects.length, projectsLoading, projectScopedId, pickedProject]);

  // Project name lookup for the "deploying to" line shown when the
  // picker is hidden (project-scoped route).
  const pickedProjectName = React.useMemo(() => {
    const p = projects.find((x) => x.id === pickedProject);
    return p?.name || '';
  }, [projects, pickedProject]);

  const openDeploy = useCallback(async (entry) => {
    if (!isAuthed) {
      navigate(`/signup?next=${encodeURIComponent('/marketplace')}`);
      return;
    }
    setActive(entry);
    setActiveDetail(entry); // optimistic — show list fields immediately
    setInstanceName('');
    setDeployError(null);
    setHoverRating(0);
    ensureProjects();
    // Fetch credit balance for the cost banner. Non-fatal — if the API
    // is unreachable the banner just shows "balance unknown" without
    // blocking deploy.
    Credits.getBalance()
      .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
      .catch(() => setCreditBalanceMc(null));
    // Refetch detail so we have myRating + fresh aggregates the moment
    // the modal opens. Non-fatal if it fails — list fields are enough.
    try {
      const fresh = await Apps.getCatalogEntry(entry.appType);
      setActiveDetail(fresh);
    } catch (e) {
      console.warn('Failed to refresh catalog detail:', e.message);
    }
  }, [isAuthed, navigate, ensureProjects]);

  // Listen for global credit-changed events so the banner stays fresh
  // if the user tops up in another tab while the modal is open.
  useEffect(() => {
    const handler = () => {
      if (!active) return;
      Credits.getBalance()
        .then((b) => setCreditBalanceMc(Number(b?.balanceMilliCents) || 0))
        .catch(() => {});
    };
    window.addEventListener(CREDIT_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CREDIT_CHANGED_EVENT, handler);
  }, [active]);

  const closeDeploy = () => {
    if (deploying) return;
    setActive(null);
    setActiveDetail(null);
    setDeployError(null);
    setHoverRating(0);
  };

  // Submit a rating. Backend recomputes the META aggregate atomically;
  // the response gives us the new avg + count without a follow-up GET.
  // Also patch the corresponding tile in `items` so the list view's
  // rating chip updates immediately without re-fetching the catalog.
  const submitRating = useCallback(async (n) => {
    if (!active?.appType || submittingRating) return;
    setSubmittingRating(true);
    try {
      const res = await Apps.rate(active.appType, { rating: n });
      const next = {
        ratingAvg: Number(res.ratingAvg) || 0,
        ratingCount: Number(res.ratingCount) || 0,
        ratingHistogram: res.ratingHistogram || activeDetail?.ratingHistogram,
      };
      setActiveDetail((prev) => prev ? {
        ...prev,
        ...next,
        myRating: { rating: n, review: '', ratedAt: new Date().toISOString() },
      } : prev);
      setItems((list) => list.map((it) => (
        it.appType === active.appType ? { ...it, ...next } : it
      )));
    } catch (e) {
      console.error('Rate failed:', e);
    } finally {
      setSubmittingRating(false);
    }
  }, [active, activeDetail, submittingRating]);

  const handleDeploy = async () => {
    if (!active || !pickedProject) return;
    setDeploying(true);
    setDeployError(null);
    try {
      const res = await Apps.deploy({
        appType: active.appType,
        projectId: pickedProject,
        name: instanceName || undefined,
      });
      const instanceId = res?.instanceId;
      if (instanceId) {
        navigate(`/projects/${pickedProject}/apps/${instanceId}`);
      } else {
        closeDeploy();
      }
    } catch (err) {
      console.error('Deploy failed:', err);
      setDeployError(err.message || 'Deploy failed');
      setDeploying(false);
    }
  };

  // Render nothing (not even the section heading) while loading or
  // when the catalog endpoint is unreachable. Local dev that hasn't
  // restarted devup.sh after pulling the catalog code will 404 on
  // GET /apps/catalog — better to hide the whole section than show a
  // scary "Not Found" panel beneath the workflow marketplace.
  if (loading || error || items.length === 0) return null;

  return (
    <>
      <section className="marketplace-section apps-section">
        {showHeader && (
          <header className="apps-section-header">
            <h2 className="apps-section-title">App Marketplace</h2>
            <p className="apps-section-subtitle">
              One-click hosted instances of popular open-source apps. Each
              instance is private to you, with an AI ops agent for install,
              monitoring, and upgrades.
            </p>
          </header>
        )}

        <ol className="marketplace-grid">
          {items.map((entry, idx) => (
            <li key={entry.appType} className="marketplace-card-li">
              <button
                type="button"
                className="marketplace-card"
                onClick={() => openDeploy(entry)}
                aria-label={`Deploy ${entry.displayName}`}
              >
                <span className="marketplace-rank">{idx + 1}</span>
                <span className="marketplace-card-icon-wrap">
                  {entry.icon ? (
                    // eslint-disable-next-line jsx-a11y/img-redundant-alt
                    <img
                      src={entry.icon}
                      alt=""
                      className="marketplace-card-icon"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="marketplace-card-icon marketplace-card-icon--fallback">
                      {(entry.displayName || entry.appType).slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </span>
                <span className="marketplace-card-body">
                  <span className="marketplace-card-name">
                    {entry.displayName}
                    <span className="apps-card-version">v{entry.appVersion}</span>
                  </span>
                  <span className="marketplace-card-tagline">{entry.tagline}</span>
                  <span className="marketplace-card-author">
                    By Zibby
                    <span className="marketplace-card-author-badge">Official</span>
                    {Number(entry.ratingCount) > 0 && (
                      <span
                        className="marketplace-card-rating"
                        aria-label={`${Number(entry.ratingAvg).toFixed(1)} stars, ${entry.ratingCount} rating${entry.ratingCount === 1 ? '' : 's'}`}
                      >
                        <IonIcon icon={star} aria-hidden="true" />
                        <span className="marketplace-card-rating-value">{Number(entry.ratingAvg).toFixed(1)}</span>
                        <span className="marketplace-card-rating-count">({entry.ratingCount})</span>
                      </span>
                    )}
                    <span className="apps-card-meta">{entry.category}</span>
                    {/* Pricing tier pill — fed by `pricing.tier` from the
                        catalog row (Agent C). Falls back to 'standard'
                        when missing so legacy catalog entries still
                        render a badge instead of nothing. */}
                    <AppPricingBadge tier={entry.pricing?.tier} className="apps-card-pricing" />
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      </section>

      {active && (
        <div className="mkt-modal-overlay" onClick={closeDeploy} role="presentation">
          <div
            className="mkt-modal"
            role="dialog"
            aria-label={`Deploy ${active.displayName}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="mkt-modal-close"
              onClick={closeDeploy}
              aria-label="Close"
              disabled={deploying}
            >×</button>

            <div className="mkt-modal-body">
              {/* Reuse the workflow-detail-modal header shape (icon
                  centered on top, title centered below) so the apps
                  modal looks like a sibling of the workflow modal. */}
              <header className="mkt-modal-header">
                {active.icon ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img src={active.icon} alt="" className="mkt-modal-icon" />
                ) : (
                  <div className="mkt-modal-icon mkt-modal-icon--fallback">
                    {(active.displayName || active.appType).slice(0, 1).toUpperCase()}
                  </div>
                )}
                <h2 className="mkt-modal-title">{active.displayName}</h2>
                <p className="mkt-modal-author">
                  By Zibby
                  <span className="mkt-modal-author-badge">Official</span>
                </p>
                <p className="mkt-modal-tagline">{active.tagline}</p>

                {/* Stats row — same `mkt-modal-stats` block the
                    workflow modal renders. Stars are clickable when
                    the user is signed in (canRate). On click we hit
                    Apps.rate; the response gives us the new aggregate
                    so the value + bucket update without a follow-up
                    GET. The hover/focus highlight pre-fills the row
                    so the user sees what they're about to commit. */}
                {(() => {
                  const detail = activeDetail || active;
                  const rating = Number(detail.ratingAvg) || 0;
                  const myRatingValue = Number(detail.myRating?.rating) || 0;
                  const canRate = isAuthed;
                  const starsFilled = hoverRating || myRatingValue || Math.round(rating);
                  return (
                    <div className="mkt-modal-stats" aria-label="Stats">
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">
                          <span className="mkt-modal-stat-number">{rating.toFixed(1)}</span>
                          <span
                            className={`mkt-modal-stat-stars ${canRate ? 'is-interactive' : ''}`}
                            role={canRate ? 'radiogroup' : undefined}
                            aria-label={canRate ? 'Rate this app' : undefined}
                            onMouseLeave={canRate ? () => setHoverRating(0) : undefined}
                          >
                            {[1, 2, 3, 4, 5].map((n) => {
                              const filled = n <= starsFilled;
                              const StarEl = canRate ? 'button' : 'span';
                              const extra = canRate ? {
                                type: 'button',
                                role: 'radio',
                                'aria-checked': n === myRatingValue,
                                'aria-label': `Rate ${n} star${n === 1 ? '' : 's'}`,
                                disabled: submittingRating,
                                onMouseEnter: () => setHoverRating(n),
                                onFocus: () => setHoverRating(n),
                                onBlur: () => setHoverRating(0),
                                onClick: () => submitRating(n),
                              } : {};
                              return (
                                <StarEl key={n} className="mkt-modal-star" {...extra}>
                                  <IonIcon icon={filled ? star : starOutline} aria-hidden="true" />
                                </StarEl>
                              );
                            })}
                          </span>
                        </div>
                        <div className="mkt-modal-stat-label">
                          {(Number(detail.ratingCount) || 0)} RATING{Number(detail.ratingCount) === 1 ? '' : 'S'}
                        </div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value mkt-modal-stat-category">
                          {active.category || '—'}
                        </div>
                        <div className="mkt-modal-stat-label">Category</div>
                      </div>
                      <div className="mkt-modal-stat">
                        <div className="mkt-modal-stat-value">v{active.appVersion}</div>
                        <div className="mkt-modal-stat-label">Version</div>
                      </div>
                    </div>
                  );
                })()}
              </header>

              {/* Cost banner — surfaces the hourly rate + current
                  balance + approx runtime hours. Drives the deploy
                  enable/disable decision below. */}
              {(() => {
                const tierInfo = resolveTier(active.pricing?.tier);
                const ratePerMin = tierInfo.milliCentsPerMin;
                const balance = creditBalanceMc;
                const hrs = balance !== null ? hoursOfRuntime(ratePerMin, balance) : null;
                const balanceStr = balance !== null ? formatBalance(balance) : '—';
                const hrsStr = hrs !== null
                  ? (hrs >= 1
                      ? `≈ ${Math.floor(hrs).toLocaleString()} hours of runtime`
                      : (hrs > 0 ? `≈ ${Math.round(hrs * 60)} min of runtime` : 'insufficient runtime'))
                  : '';
                return (
                  <div className="apps-modal-cost-banner">
                    <strong>This app costs {tierInfo.hourly} while running.</strong>
                    {' '}Your balance: <strong>{balanceStr}</strong>
                    {hrsStr ? ` (${hrsStr})` : ''}
                  </div>
                );
              })()}

              {active.ui?.setupHint && (
                <p className="apps-modal-hint">
                  <strong>Heads up:</strong> {active.ui.setupHint}
                </p>
              )}

              <ul className="apps-modal-facts">
                <li><strong>Public URL</strong> <span>https://<em>{'<id>'}</em>.apps.zibby.dev</span></li>
                <li><strong>Resources</strong> <span>{(active.resources.cpu / 1024).toFixed(2)} vCPU · {(active.resources.memory / 1024).toFixed(2)} GB RAM</span></li>
                <li><strong>Cost</strong> <span>$29 / month (added to your Zibby subscription)</span></li>
                {active.homepage && (
                  <li>
                    <strong>Homepage</strong>
                    <span>
                      <a
                        href={active.homepage}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="apps-modal-homepage-link"
                      >
                        <IonIcon icon={openOutline} aria-hidden="true" />
                        {active.homepage.replace(/^https?:\/\//, '')}
                      </a>
                    </span>
                  </li>
                )}
              </ul>

              {deployError && (
                <div className="apps-modal-error">
                  <IonIcon icon={alertCircle} aria-hidden="true" /> {deployError}
                </div>
              )}
            </div>

            {/* Footer mirrors the workflow Deploy footer's shape:
                label row + input/control row. Apps have no agent
                picker (the daemon is fixed) and no warm-pool (one
                instance per row, not Fargate-scaled), so the controls
                here are just: project picker (when not URL-scoped) OR
                read-only "Deploying to" line + instance-name field +
                Deploy button. Same `mkt-modal-name-*` classes as the
                workflow footer so the styling rides along for free. */}
            <footer className="mkt-modal-footer apps-modal-footer">
              <div className="mkt-modal-name-label-row">
                <label className="mkt-modal-name-label" htmlFor="apps-deploy-name-input">
                  Instance name
                </label>
                <p className="mkt-modal-name-help is-valid" aria-live="polite">
                  {pickedProjectName
                    ? `Available — will deploy to ${pickedProjectName}`
                    : (projectsLoading ? 'Loading project…' : '')}
                </p>
              </div>
              <div className="mkt-modal-name-row is-valid">
                {/* Same Field atom the workflow footer uses — the
                    status="ok" prop is what renders the green check
                    icon + green border. Apps don't have a name-
                    uniqueness check (instanceId is auto-generated)
                    so the field is always "ok" once the user can
                    submit. */}
                <Field
                  id="apps-deploy-name-input"
                  type="text"
                  className="mkt-modal-name-input"
                  value={instanceName}
                  onChange={(e) => setInstanceName(e.target.value.slice(0, 80))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && pickedProject && !deploying) {
                      e.preventDefault();
                      handleDeploy();
                    }
                  }}
                  placeholder={active.displayName}
                  autoComplete="off"
                  spellCheck="false"
                  disabled={deploying}
                  status="ok"
                />
                {!projectScopedId && (
                  <select
                    className="mkt-field mkt-field--select apps-modal-footer-project"
                    value={pickedProject}
                    onChange={(e) => setPickedProject(e.target.value)}
                    disabled={projectsLoading || deploying}
                    aria-label="Project for this instance"
                  >
                    {projectsLoading && <option value="">Loading…</option>}
                    {!projectsLoading && projects.length === 0 && (
                      <option value="">No projects</option>
                    )}
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                )}
                {/* Wrapping the Deploy button in `mkt-modal-deploy-split`
                    (without a chevron) gives it the same inline-block
                    sizing the workflow footer uses, so it doesn't
                    stretch to fill the whole row. */}
                {(() => {
                  // Insufficient-credits check: < 5 minutes of the app's
                  // hourly rate. Balance unknown (null) doesn't block —
                  // we trust the backend's own check at deploy time
                  // rather than blocking on a missing fetch.
                  const tierInfo = resolveTier(active.pricing?.tier);
                  const fiveMinCost = tierInfo.milliCentsPerMin * 5;
                  const insufficient = creditBalanceMc !== null
                    && creditBalanceMc < fiveMinCost;
                  return (
                    <span className="mkt-modal-deploy-split">
                      <button
                        type="button"
                        className="mkt-modal-deploy"
                        disabled={!pickedProject || deploying || insufficient}
                        onClick={handleDeploy}
                        title={insufficient ? 'Add credits to deploy' : undefined}
                      >
                        <IonIcon icon={rocketOutline} aria-hidden="true" />
                        {deploying ? 'Deploying…' : 'Deploy'}
                      </button>
                    </span>
                  );
                })()}
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
};

export default AppsMarketplaceSection;
